//client/src/components/plataforma/siteInstitucional/FaqPage.js
import React,{ useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFaqs } from '../../../hooks/useFaqs';
import useAlert from '../../../hooks/useAlert'; // ✅ Importado
import { Plus, HelpCircle, Edit, Trash2, Loader2, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader'; // ✅ Importado
import FabButton from '../../common/FabButton';
import FormModal from '../../layout/FormModal'; // ✅ Importado
import ActionsDrawer from '../../common/ActionsDrawer';
import { useHomePersonalizadaGate, HomePersonalizadaUpsellBanner } from './useHomePersonalizadaGate';

const FaqPage = () => {
    const navigate = useNavigate();
    const { hasHomePersonalizada } = useHomePersonalizadaGate();
    const { faqs, loading, error, deleteFaq } = useFaqs();
    const { setAlert } = useAlert(); // ✅ Importado
    const [activeCategory, setActiveCategory] = useState('all');
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, faqId: null, faqQuestion: '' });
    const [selectedFaq, setSelectedFaq] = useState(null);

    const openDeleteModal = (id, question) => {
        setDeleteModal({ isOpen: true, faqId: id, faqQuestion: question });
        setSelectedFaq(null);
    };

    const handleEditFaq = (faq) => {
        setSelectedFaq(null);
        navigate(`/site-institucional/faq/editar/${faq._id}`);
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, faqId: null, faqQuestion: '' });
    };

    const confirmDelete = async () => {
        if (!deleteModal.faqId) return;
        try {
            await deleteFaq(deleteModal.faqId);
            setAlert('FAQ excluída com sucesso!', 'success');
        } catch (err) {
            setAlert(`Erro ao excluir FAQ: ${err.message}`, 'error');
        } finally {
            closeDeleteModal();
        }
    };

    const categories = useMemo(() => {
        const uniqueCategories = ['all', ...new Set(faqs.map(faq => faq.category))];
        return uniqueCategories;
    }, [faqs]);

    const filteredFaqs = activeCategory === 'all' 
        ? faqs 
        : faqs.filter(faq => faq.category === activeCategory);

    if (loading && faqs.length === 0) {
        return (
            <div className="page-management-container flex justify-center items-center h-64">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-management-container">
                <div className="alert alert-danger">{error}</div>
            </div>
        );
    }

    return (
        <div className="page-management-container">
            {/* ✅ Cabeçalho antigo substituído pelo componente PageHeader */}
            <PageHeader
                title="FAQs"
                icon={<HelpCircle className="icon-title" />}
            />

            {!hasHomePersonalizada && <HomePersonalizadaUpsellBanner />}

            <div className="content-panel mt-6">
                <div className="mb-6">
                    <h3 className="text-lg font-medium mb-3">Filtrar por categoria</h3>
                    <div className="flex flex-wrap gap-2">
                        {categories.map(category => (
                            <button
                                key={category}
                                type="button"
                                className={`px-3 py-1 rounded-full text-sm font-medium capitalize transition-colors ${
                                    activeCategory === category
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                onClick={() => setActiveCategory(category)}
                            >
                                {category === 'all' ? 'Todas' : category}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-4">
                    {filteredFaqs.map((faq) => (
                        <div key={faq._id} className="border rounded-lg p-4 bg-white shadow-sm">
                            <div className="flex justify-between items-start">
                                <div className="flex-1 pr-4">
                                    <h4 className="font-semibold text-lg text-gray-800">{faq.question}</h4>
                                    <p className="text-gray-600 mt-2">{faq.answer}</p>
                                    <div className="flex items-center mt-3 gap-3">
                                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded capitalize font-medium">
                                            {faq.category}
                                        </span>
                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                            faq.status === 'published'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {faq.status === 'published' ? 'Publicado' : 'Rascunho'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-2 ml-4">
                                    <button
                                        onClick={() => setSelectedFaq(faq)}
                                        className="action-icon-button text-gray-600"
                                        title="Ações"
                                        disabled={!hasHomePersonalizada}
                                    >
                                        <MoreVertical size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {filteredFaqs.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            Nenhuma FAQ encontrada para esta categoria.
                        </div>
                    )}
                </div>
            </div>
            
            {/* ✅ Modal de confirmação de exclusão */}
            {deleteModal.isOpen && (
                <FormModal title="Confirmar Exclusão" onClose={closeDeleteModal} size="small">
                    <p>Você tem certeza que deseja excluir a pergunta?</p>
                    <p className="text-sm bg-gray-100 p-2 rounded mt-2">"<strong>{deleteModal.faqQuestion}</strong>"</p>
                    <div className="form-actions">
                        <button type="button" onClick={closeDeleteModal} className="action-button action-button-secondary">Cancelar</button>
                        <button type="button" onClick={confirmDelete} className="action-button action-button-danger">Confirmar Exclusão</button>
                    </div>
                </FormModal>
            )}

            <ActionsDrawer
                open={!!selectedFaq}
                onClose={() => setSelectedFaq(null)}
                title={selectedFaq?.question || 'FAQ'}
                actions={selectedFaq ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEditFaq(selectedFaq),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => openDeleteModal(selectedFaq._id, selectedFaq.question),
                        destructive: true,
                    },
                ] : []}
            />

            <FabButton onClick={() => navigate('/site-institucional/faq/nova')} title="Nova FAQ" icon={<HelpCircle size={26} className="icon" />} disabled={!hasHomePersonalizada} />
        </div>
    );
};

export default FaqPage;